/**
 * vocab.ts — Display labels and canonical ordering for Period / Movement / Genre.
 *
 * The TYPE-level closed unions live in schema.ts (so the compiler catches
 * typos in author JSONs). This file ships the human-readable labels (EN + IT)
 * and the order in which filter UIs render values.
 *
 * Adding a new vocab value: add it to BOTH the union in schema.ts AND the
 * entry here. The render layer reads from here exclusively.
 */

import type { Period, Movement, Genre, ThemeId } from './schema';

export interface VocabEntry<T extends string> {
  id: T;
  label_en: string;
  label_it: string;
  order: number;
}

// ══════ PERIODS (chronological, UK + US interleaved) ══════

export const PERIOD_VOCAB: ReadonlyArray<VocabEntry<Period>> = [
  { id: 'eta-arcaica',          label_en: 'Archaic age',                   label_it: 'Età arcaica',                     order: 1 },
  { id: 'eta-repubblicana',     label_en: 'Republican age',                label_it: 'Età repubblicana',                order: 2 },
  { id: 'eta-augustea',         label_en: 'Augustan age',                  label_it: 'Età augustea',                    order: 3 },
  { id: 'eta-giulio-claudia',   label_en: 'Julio-Claudian age',            label_it: 'Età giulio-claudia',              order: 4 },
  { id: 'eta-flavia-traianea',  label_en: 'Flavian and Trajanic age',      label_it: 'Età flavia e traianea',           order: 5 },
  { id: 'eta-antonina',         label_en: 'Antonine age',                  label_it: 'Età antonina',                    order: 6 },
  { id: 'eta-tardoantica',      label_en: 'Late antique and Christian age', label_it: 'Età tardoantica e cristiana',     order: 7 },
];

// ══════ MOVEMENTS ══════

export const MOVEMENT_VOCAB: ReadonlyArray<VocabEntry<Movement>> = [
  { id: 'ellenismo',              label_en: 'Hellenism',                     label_it: 'Ellenismo',                       order: 1 },
  { id: 'circolo-scipionico',     label_en: 'Scipionic Circle',              label_it: 'Circolo degli Scipioni',          order: 2 },
  { id: 'neoterismo',             label_en: 'Neotericism',                   label_it: 'Neoterismo',                      order: 3 },
  { id: 'epicureismo',            label_en: 'Epicureanism',                  label_it: 'Epicureismo',                     order: 4 },
  { id: 'stoicismo',              label_en: 'Stoicism',                      label_it: 'Stoicismo',                       order: 5 },
  { id: 'classicismo-augusteo',   label_en: 'Augustan classicism',           label_it: 'Classicismo augusteo',            order: 6 },
  { id: 'alessandrinismo',        label_en: 'Alexandrianism',                label_it: 'Alessandrinismo',                 order: 7 },
  { id: 'arcaismo',               label_en: 'Archaism',                      label_it: 'Arcaismo',                        order: 8 },
  { id: 'retorica-declamatoria',  label_en: 'Declamatory rhetoric',          label_it: 'Retorica declamatoria',           order: 9 },
  { id: 'patristica',             label_en: 'Patristics',                    label_it: 'Patristica',                      order: 10 },
];

// ══════ GENRES / FORMS ══════

export const GENRE_VOCAB: ReadonlyArray<VocabEntry<Genre>> = [
  { id: 'epica',              label_en: 'Epic',                          label_it: 'Epica',                           order: 1 },
  { id: 'tragedia',           label_en: 'Tragedy',                       label_it: 'Tragedia',                        order: 2 },
  { id: 'commedia',           label_en: 'Comedy',                        label_it: 'Commedia',                        order: 3 },
  { id: 'satira',             label_en: 'Satire',                        label_it: 'Satira',                          order: 4 },
  { id: 'elegia',             label_en: 'Elegy',                         label_it: 'Elegia',                          order: 5 },
  { id: 'lirica',             label_en: 'Lyric',                         label_it: 'Lirica',                          order: 6 },
  { id: 'epigramma',          label_en: 'Epigram',                       label_it: 'Epigramma',                       order: 7 },
  { id: 'favola',             label_en: 'Fable',                         label_it: 'Favola',                          order: 8 },
  { id: 'poema-didascalico',  label_en: 'Didactic poem',                 label_it: 'Poema didascalico',               order: 9 },
  { id: 'storiografia',       label_en: 'Historiography',                label_it: 'Storiografia',                    order: 10 },
  { id: 'biografia',          label_en: 'Biography',                     label_it: 'Biografia',                       order: 11 },
  { id: 'oratoria',           label_en: 'Oratory',                       label_it: 'Oratoria',                        order: 12 },
  { id: 'epistolografia',     label_en: 'Letters',                       label_it: 'Epistolografia',                  order: 13 },
  { id: 'trattato',           label_en: 'Treatise',                      label_it: 'Trattato',                        order: 14 },
  { id: 'dialogo',            label_en: 'Dialogue',                      label_it: 'Dialogo',                         order: 15 },
  { id: 'romanzo',            label_en: 'Novel',                         label_it: 'Romanzo',                         order: 16 },
  { id: 'apologetica',        label_en: 'Apologetics',                   label_it: 'Apologetica',                     order: 17 },
];

// ══════ Lookup helpers ══════

const PERIOD_BY_ID = new Map(PERIOD_VOCAB.map(e => [e.id, e]));
const MOVEMENT_BY_ID = new Map(MOVEMENT_VOCAB.map(e => [e.id, e]));
const GENRE_BY_ID = new Map(GENRE_VOCAB.map(e => [e.id, e]));

export type UiLocale = 'en' | 'it';

export function periodLabel(id: Period, locale: UiLocale): string {
  const e = PERIOD_BY_ID.get(id);
  return e ? (locale === 'en' ? e.label_en : e.label_it) : id;
}

export function movementLabel(id: Movement, locale: UiLocale): string {
  const e = MOVEMENT_BY_ID.get(id);
  return e ? (locale === 'en' ? e.label_en : e.label_it) : id;
}

export function genreLabel(id: Genre, locale: UiLocale): string {
  const e = GENRE_BY_ID.get(id);
  return e ? (locale === 'en' ? e.label_en : e.label_it) : id;
}

/** Theme id is Movement | Genre — resolves in either vocab. */
export function themeLabel(id: ThemeId, locale: UiLocale): string {
  const mv = MOVEMENT_BY_ID.get(id as Movement);
  if (mv) return locale === 'en' ? mv.label_en : mv.label_it;
  const gn = GENRE_BY_ID.get(id as Genre);
  if (gn) return locale === 'en' ? gn.label_en : gn.label_it;
  return id;
}

/** "movement" | "genre" | "unknown" — useful for the ThemeMapView lens. */
export function themeKind(id: ThemeId): 'movement' | 'genre' | 'unknown' {
  if (MOVEMENT_BY_ID.has(id as Movement)) return 'movement';
  if (GENRE_BY_ID.has(id as Genre)) return 'genre';
  return 'unknown';
}

/**
 * Etichetta localizzata del TIPO di un nodo mappa (opera / movimento / genere /
 * tema). Usata come caption nei nodi della mappa autore e della mappa temi:
 * prima erano stringhe inglesi hardcoded ("Work ▶", "GENRE") anche con UI in
 * italiano. 'unknown' ricade su "tema".
 */
const NODE_KIND_LABELS: Record<string, { it: string; en: string }> = {
  work:     { it: 'Opera',     en: 'Work' },
  movement: { it: 'Movimento', en: 'Movement' },
  genre:    { it: 'Genere',    en: 'Genre' },
  theme:    { it: 'Tema',      en: 'Theme' },
  unknown:  { it: 'Tema',      en: 'Theme' },
};

export function nodeKindLabel(kind: string, locale: UiLocale): string {
  const e = NODE_KIND_LABELS[kind];
  return e ? (locale === 'en' ? e.en : e.it) : kind;
}
